import { useNavigate, useParams } from 'react-router-dom';

import { Honeybadger } from '@honeybadger-io/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { upsertResponse } from 'api/CustomForms';

import { responseUpdated } from 'lib/helpers/utility';

import { showAlert } from 'components/shared/Alert/Alert';

export const useAdvancedFormSubmit = ({
  noteType,
  formDataKey,
  queryKey,
  updateForm,
  setAdvancedNote,
  setSaveButtonText,
  updateSaveButton,
  setFormUpdated,
  setNarrativeUpdated,
  oldForm,
  currentForm,
  formUpdated,
  narrativeUpdated
}) => {
  const navigate = useNavigate();
  const { id, appointmentId } = useParams();

  const queryClient = useQueryClient();

  const submitMutation = useMutation({
    mutationFn: (data) => upsertResponse(navigate, data),
    onMutate: () => {
      setSaveButtonText('Saving');
    },
    onSettled: () => {
      updateSaveButton();
    },
    onSuccess: (data) => {
      const { upsert } = data || {};
      if (upsert) {
        setFormUpdated(false);
        setNarrativeUpdated(false);
        updateForm(upsert);

        const currentData = queryClient.getQueryData(queryKey);

        if (currentData?.data) {
          const newData = {
            ...currentData,
            data: { ...currentData.data, formResponse: { ...upsert } }
          };
          queryClient.setQueryData(queryKey, newData);
        }

        setAdvancedNote((prev) => {
          let newData = prev;

          if (formDataKey === 'advancedCustomNote') {
            newData = { ...prev };

            const existingFormIndex = newData[noteType].findIndex((form) =>
              [upsert.form_id, upsert.id].includes(form.id)
            );

            if (existingFormIndex > -1) {
              newData[noteType][existingFormIndex] = {
                ...newData[noteType][existingFormIndex],
                ...upsert
              };
            }
          } else {
            newData = [...prev];

            const existingFormIndex = newData.findIndex((form) =>
              [upsert.form_id, upsert.id].includes(form.id)
            );

            if (existingFormIndex > -1) {
              newData[existingFormIndex] = { ...newData[existingFormIndex], ...upsert };
            }
          }

          return newData;
        });
      } else {
        showAlert({
          title: 'Necessary permissions not present or error encountered',
          color: 'danger'
        });
      }
    },
    onError: (error) => {
      Honeybadger.notify(`file: useAdvancedFormSubmit.js, auto-save error: ${error}`);
    }
  });

  const handleFormSubmit = async (e, responseId, formId, formVersion) => {
    try {
      const updatedResponses = e?.map((response) => {
        return response.name
          ? { ...response }
          : { ...response, name: 'no_name', custom_name: 'no_custom_name' };
      });

      // Macro-applied forms are persisted by the ClinicalNoteContext effect
      // (the single owner of forceSave.advancedForm); this path only handles
      // user edits, so gate purely on change detection.
      if (!formUpdated && !narrativeUpdated) return;

      if (formUpdated) {
        const hasFormChanged = !responseUpdated({
          currentForm: oldForm,
          submitResponse: updatedResponses
        });

        if (hasFormChanged) {
          return;
        }
      }

      const params = {
        responseId,
        formId,
        json: { fields: JSON.stringify(updatedResponses) },
        appointmentId,
        patientId: id,
        formVersion,
        isNotClinicalNote: false,
        narrative: currentForm?.narrative,
        simple_narrative: currentForm?.simple_narrative
      };

      submitMutation.mutate(params);
    } catch (error) {
      Honeybadger.notify(`file: useAdvancedFormSubmit.js.jsx, error: ${error}`);
    }
  };

  return { handleFormSubmit };
};
